import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaPaperclip,
  FaPaperPlane,
  FaSpinner,
  FaCheckCircle,
  FaFile,
  FaTimes,
  FaMicrophone,
  FaStop,
  FaPlay,
  FaPause,
} from "react-icons/fa";
import { getEngagementChatHistory } from "../api/serviceEngagements";
import { buildApiUrl, buildWsUrl } from "../helpers/base_url";

// Voice Message Player Component
const VoiceMessagePlayer: React.FC<{
  audioUrl: string;
  isOwnMessage: boolean;
}> = ({ audioUrl, isOwnMessage }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioUrl) return;
    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.src = "";
    };
  }, [audioUrl]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      style={{
        marginTop: 6,
        display: "flex",
        alignItems: "center",
        gap: 12,
        minWidth: 200,
        maxWidth: 280,
      }}
    >
      <button
        onClick={togglePlay}
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "none",
          background: isOwnMessage
            ? "rgba(255, 255, 255, 0.25)"
            : "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
          color: isOwnMessage ? "#fff" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
        }}
      >
        {isPlaying ? (
          <FaPause size={14} />
        ) : (
          <FaPlay size={14} style={{ marginLeft: 2 }} />
        )}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            height: 4,
            background: isOwnMessage
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(108, 99, 255, 0.2)",
            borderRadius: 2,
            overflow: "hidden",
            marginBottom: 6,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: isOwnMessage
                ? "#fff"
                : "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
              borderRadius: 2,
              transition: "width 0.1s linear",
            }}
          />
        </div>
        <div
          style={{
            fontSize: 11,
            color: isOwnMessage
              ? "rgba(255, 255, 255, 0.85)"
              : "#666",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
};

const EngagementChatPage = () => {
  const { engagementId } = useParams();
  const navigate = useNavigate();
  const [chatWebSocket, setChatWebSocket] = useState<any>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isLoadingOlderMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Fetch user from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const userId = user?.id;

  const showChatNotification = useCallback(
    (title: string, body: string, url?: string) => {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission !== "granted") return;

      try {
        const notification = new Notification(title, {
          body,
          icon: "/logo192.png",
          data: { url },
        });
        notification.onclick = () => {
          window.focus();
          if (url) {
            navigate(url);
          }
          notification.close();
        };
      } catch (error) {
        console.warn("Notification display failed:", error);
      }
    },
    [navigate]
  );

  // Fetch messages from REST API
  const fetchMessages = useCallback(async (page = 0, size = 20) => {
    if (!engagementId) return;
    try {
      setLoadingMessages(true);
      const data = await getEngagementChatHistory(
        Number(engagementId),
        page,
        size
      );
      if (page === 0) {
        // API returns messages - sort to ensure oldest first, newest last
        const messages = data.messages || [];
        const sorted = [...messages].sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeA - timeB; // Oldest first
        });
        setChatMessages(sorted);
      } else {
        // For subsequent pages, merge and sort all messages
        setChatMessages((prev) => {
          const merged = [...prev, ...(data.messages || [])];
          return merged.sort((a, b) => {
            const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return timeA - timeB; // Oldest first
          });
        });
      }
    } catch (error: any) {
      // swallow; UI currently shows loading/empty states
    } finally {
      setLoadingMessages(false);
    }
  }, [engagementId]);

  useEffect(() => {
    if (engagementId) fetchMessages(0, 20);
  }, [engagementId, fetchMessages]);

  // WebSocket connection
  useEffect(() => {
    if (!engagementId || !userId) return;
    setChatLoading(true);
    setConnectionStatus("connecting");
    const wsPath = process.env.REACT_APP_WS_CHAT_PATH || "/ws/chat";
    // Construct WebSocket URL matching the working format: wss://api.eventnova.com.ng/ws/chat?userId=2
    const wsUrl = `${buildWsUrl(wsPath)}?userId=${userId}`;
    console.log("Connecting to WebSocket:", wsUrl); // Debug log
    const ws = new window.WebSocket(wsUrl);
    setChatWebSocket(ws);
    ws.onopen = () => {
      setConnectionStatus("connected");
      setChatLoading(false);
      ws.send(
        JSON.stringify({
          type: "JOIN_ENGAGEMENT",
          engagementId: Number(engagementId),
        })
      );
    };
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        switch (message.type) {
          case "RECENT_MESSAGES":
            if (chatMessages.length === 0) {
              const messages = message.data || [];
              // Sort messages chronologically (oldest first)
              const sorted = [...messages].sort((a, b) => {
                const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return timeA - timeB; // Oldest first
              });
              setChatMessages(sorted);
            }
            break;
          case "NEW_MESSAGE": {
            const incomingMessage = message.data;
            // Add new message and sort to maintain chronological order
            setChatMessages((prev) => {
              const updated = [...prev, incomingMessage];
              return updated.sort((a, b) => {
                const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return timeA - timeB; // Oldest first
              });
            });
            if (
              incomingMessage &&
              incomingMessage.senderId &&
              incomingMessage.senderId !== userId &&
              typeof document !== "undefined" &&
              document.visibilityState === "hidden"
            ) {
              showChatNotification(
                incomingMessage.senderName || "New message",
                incomingMessage.content || "You have a new chat message.",
                engagementId ? `/engagement-chat/${engagementId}` : undefined
              );
            }
            break;
          }
          case "TYPING_STATUS":
            setOtherUserTyping(message.data.isTyping);
            break;
          default:
            break;
        }
      } catch (error) {
        // Ignore
      }
    };
    ws.onerror = () => {
      setConnectionStatus("error");
    };
    ws.onclose = () => {
      setConnectionStatus("disconnected");
      setOtherUserTyping(false);
    };
    setChatLoading(false);
    return () => ws.close();
    // eslint-disable-next-line
  }, [engagementId, userId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (
      messagesEndRef.current &&
      chatMessages.length > 0 &&
      !isLoadingOlderMessages
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages.length, isLoadingOlderMessages]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [isRecording, audioUrl]);

  // Send message
  const sendChatMessage = () => {
    if (!chatInput.trim() || !chatWebSocket) return;
    chatWebSocket.send(
      JSON.stringify({
        type: "SEND_MESSAGE",
        engagementId: Number(engagementId),
        content: chatInput.trim(),
        messageType: "TEXT",
      })
    );
    setChatInput("");
  };

  // Typing status
  const sendTypingStatus = (isTyping: boolean) => {
    if (!chatWebSocket) return;
    chatWebSocket.send(
      JSON.stringify({
        type: "TYPING",
        engagementId: Number(engagementId),
        isTyping,
      })
    );
  };

  // File upload
  const handleFileSelect = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };
  const getMessageType = (file: any) => {
    if (!file) return "TEXT";
    const fileType = file.type.toLowerCase();
    if (fileType.startsWith("image/")) return "IMAGE";
    if (fileType.startsWith("video/")) return "VIDEO";
    if (fileType.startsWith("audio/")) return "AUDIO";
    return "FILE";
  };
  const uploadAndSendFile = async () => {
    if (!selectedFile || !chatWebSocket) return;
    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      const token = localStorage.getItem("accessToken");
      const response = await fetch(buildApiUrl("/file/"), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
      });
      if (!response.ok) throw new Error("File upload failed");
      const uploadResult = await response.json();
      if (!uploadResult.success || !uploadResult.data) {
        throw new Error("File upload failed");
      }
      chatWebSocket.send(
        JSON.stringify({
          type: "SEND_MESSAGE",
          engagementId: Number(engagementId),
          content: selectedFile.name,
          messageType: getMessageType(selectedFile),
          fileUrl: uploadResult.data,
        })
      );
      setSelectedFile(null);
      setChatInput("");
    } catch (error) {
      alert("Failed to upload file. Please try again.");
    } finally {
      setUploadingFile(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Failed to access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setRecordingTime(0);
  };

  const uploadAndSendVoiceMessage = async () => {
    if (!audioBlob || !chatWebSocket) return;
    try {
      setIsUploadingVoice(true);
      const formData = new FormData();
      formData.append("file", audioBlob, "voice-message.webm");
      const token = localStorage.getItem("accessToken");
      const response = await fetch(buildApiUrl("/file/"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) throw new Error("Voice upload failed");
      const uploadResult = await response.json();
      if (!uploadResult.success || !uploadResult.data) {
        throw new Error("Voice upload failed");
      }
      
      // Use attachmentUrl for voice messages as per the payload format
      chatWebSocket.send(
        JSON.stringify({
          type: "SEND_MESSAGE",
          engagementId: Number(engagementId),
          content: "[Voice Message]",
          messageType: "VOICE",
          attachmentUrl: uploadResult.data,
        })
      );
      
      // Cleanup
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
    } catch (error) {
      alert("Failed to upload voice message. Please try again.");
    } finally {
      setIsUploadingVoice(false);
    }
  };

  // Elegant timestamp parser
  const parseTimestamp = (timestamp: any) => {
    if (!timestamp) return "Just now";

    try {
      let date: Date | null = null;

      // Handle string that looks like an array representation (e.g., "2025,11,17,17,46,9,6")
      // This can happen if an array was stringified
      if (typeof timestamp === "string" && /^\d+,\d+,\d+/.test(timestamp)) {
        const parts = timestamp
          .split(",")
          .map((p: string) => parseInt(p.trim(), 10));
        if (parts.length >= 6 && parts.every((p: number) => !isNaN(p))) {
          const [year, month, day, hour, minute, second, milliseconds] = parts;
          // Validate values before creating date
          if (
            year >= 1970 &&
            year <= 2100 &&
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            day <= 31
          ) {
            date = new Date(
              year,
              month - 1,
              day,
              hour || 0,
              minute || 0,
              second || 0,
              milliseconds || 0
            );
            // Verify the date is valid
            if (isNaN(date.getTime())) {
              date = null;
            } else {
              // Successfully parsed, skip other checks
            }
          }
        }
      }

      // Handle array format like [2025, 11, 17, 17, 46, 9, 6]
      if (!date && Array.isArray(timestamp)) {
        // Array format: [year, month, day, hour, minute, second, milliseconds?]
        if (timestamp.length >= 6) {
          const [year, month, day, hour, minute, second, milliseconds] =
            timestamp;
          // Validate values before creating date
          if (
            year >= 1970 &&
            year <= 2100 &&
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            day <= 31
          ) {
            date = new Date(
              year,
              month - 1,
              day,
              hour || 0,
              minute || 0,
              second || 0,
              milliseconds || 0
            );
            // Verify the date is valid
            if (isNaN(date.getTime())) {
              date = null;
            }
          }
        }
      }
      // Handle other comma-separated strings (if not already handled above)
      else if (
        !date &&
        typeof timestamp === "string" &&
        timestamp.includes(",")
      ) {
        const parts = timestamp
          .split(",")
          .map((p: string) => parseInt(p.trim(), 10));
        if (parts.length >= 6 && parts.every((p: number) => !isNaN(p))) {
          const [year, month, day, hour, minute, second, milliseconds] = parts;
          // Validate values before creating date
          if (
            year >= 1970 &&
            year <= 2100 &&
            month >= 1 &&
            month <= 12 &&
            day >= 1 &&
            day <= 31
          ) {
            date = new Date(
              year,
              month - 1,
              day,
              hour || 0,
              minute || 0,
              second || 0,
              milliseconds || 0
            );
            // Verify the date is valid
            if (isNaN(date.getTime())) {
              date = null;
            }
          }
        }
      }
      // Handle numeric timestamps (Unix timestamp in milliseconds or seconds)
      else if (typeof timestamp === "number") {
        // If timestamp is in seconds (less than year 2000 in milliseconds), convert to milliseconds
        if (timestamp < 946684800000) {
          date = new Date(timestamp * 1000);
        } else {
          date = new Date(timestamp);
        }
      }
      // Handle string timestamps
      else if (typeof timestamp === "string") {
        // Try parsing as ISO string first
        date = new Date(timestamp);

        // If that fails, try replacing space with 'T' for ISO format
        if (isNaN(date.getTime())) {
        date = new Date(timestamp.replace(" ", "T"));
      }

        // If still invalid, check if it's a long numeric string
        if (isNaN(date.getTime()) && /^\d+$/.test(timestamp)) {
          const num = parseInt(timestamp, 10);

          // Check if it's a concatenated date format like "2025111717469620963000" (YYYYMMDDHHmmss...)
          if (timestamp.length >= 14) {
            // Try parsing as YYYYMMDDHHmmss format
            const year = parseInt(timestamp.substring(0, 4), 10);
            const month = parseInt(timestamp.substring(4, 6), 10) - 1; // Month is 0-indexed
            const day = parseInt(timestamp.substring(6, 8), 10);
            const hour = parseInt(timestamp.substring(8, 10), 10);
            const minute = parseInt(timestamp.substring(10, 12), 10);
            const second =
              timestamp.length >= 14
                ? parseInt(timestamp.substring(12, 14), 10)
                : 0;

            // Validate the parsed values (allow seconds > 59 as Date constructor handles overflow)
            if (
              year >= 1970 &&
              year <= 2100 &&
              month >= 0 &&
              month < 12 &&
              day >= 1 &&
              day <= 31 &&
              hour >= 0 &&
              hour < 24 &&
              minute >= 0 &&
              minute < 60 &&
              second >= 0 &&
              second < 100
            ) {
              const parsedDate = new Date(
                year,
                month,
                day,
                hour,
                minute,
                second
              );
              if (!isNaN(parsedDate.getTime())) {
                date = parsedDate;
              }
            }
          }

          // If concatenated format didn't work, try other numeric formats
          if (!date || isNaN(date.getTime())) {
            if (timestamp.length > 13) {
              // Assume nanoseconds, convert to milliseconds
              date = new Date(num / 1000000);
            } else if (timestamp.length > 10) {
              // Assume microseconds, convert to milliseconds
              date = new Date(num / 1000);
            } else if (timestamp.length === 10) {
              // Unix timestamp in seconds
              date = new Date(num * 1000);
            } else {
              // Try as milliseconds
              date = new Date(num);
            }
          }
        }
      }

      // If we have a valid date, format it nicely
      if (date && !isNaN(date.getTime())) {
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();

        // If date is in the future (negative diff), show the actual time
        if (diffMs < 0) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        }

        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        // Show relative time for recent messages (only if less than 1 minute)
        if (diffMins < 1 && diffMs >= 0) return "Just now";
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays}d ago`;

        // For older messages, show formatted date
        const isToday = date.toDateString() === now.toDateString();
        const isYesterday =
          date.toDateString() ===
          new Date(now.getTime() - 86400000).toDateString();

        if (isToday) {
          return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        } else if (isYesterday) {
          return `Yesterday ${date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}`;
        } else {
          return date.toLocaleString([], {
            month: "short",
            day: "numeric",
            year:
              date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }

      // Fallback: if we couldn't parse it, try one more time with a generic date parser
      // If it's still a string or array, try to format it as a date string
      if (typeof timestamp === "string" || Array.isArray(timestamp)) {
        // Last resort: try to create a date from the string representation
        const dateStr = Array.isArray(timestamp)
          ? timestamp.join(",")
          : timestamp;
        const testDate = new Date(dateStr);
        if (!isNaN(testDate.getTime())) {
          const now = new Date();
          const diffMs = now.getTime() - testDate.getTime();
          const diffMins = Math.floor(diffMs / 60000);

          // Only show "Just now" if actually recent
          if (diffMins < 1 && diffMs >= 0) {
            return "Just now";
          }

          return testDate.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }

      // Final fallback: try to show the timestamp as-is if it's a readable format
      // Otherwise show a generic time format
      if (typeof timestamp === "string" && timestamp.length > 0) {
        // If it's a readable string, show it
        if (timestamp.length < 50) {
      return timestamp;
        }
      }

      // Last resort: show a generic message
      return "Time unavailable";
    } catch (error) {
      // If all parsing fails, return a safe fallback
      console.warn("Failed to parse timestamp:", timestamp, error);
      return "Just now";
    }
  };

  // Get user info for avatar
  const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
  const userInitials = userInfo?.firstName?.[0]?.toUpperCase() || "U";

  // Memoized sorted messages - always oldest first, newest last
  const sortedMessages = useMemo(() => {
    if (chatMessages.length === 0) return [];
    
    // Sort messages by createdAt timestamp (ascending = oldest first)
    const sorted = [...chatMessages].sort((a, b) => {
      const parseTime = (timestamp: any): number => {
        if (!timestamp) return 0;
        try {
          const date = new Date(timestamp);
          return isNaN(date.getTime()) ? 0 : date.getTime();
        } catch {
          return 0;
        }
      };
      const timeA = parseTime(a.createdAt);
      const timeB = parseTime(b.createdAt);
      
      // If both have valid timestamps, sort descending (newest first), then we'll reverse
      if (timeA > 0 && timeB > 0) {
        return timeB - timeA; // Descending: newest first
      }
      // If one has timestamp and other doesn't, put timestamped one first
      if (timeA > 0 && timeB === 0) return -1;
      if (timeB > 0 && timeA === 0) return 1;
      // If neither has timestamp, maintain original order
      return 0;
    });
    
    // After sorting descending (newest first), reverse to get oldest first
    // This ensures oldest messages appear at the top, newest at the bottom
    return sorted.reverse();
  }, [chatMessages]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 100%)",
        backgroundSize: "400% 400%",
        animation: "gradientShift 15s ease infinite",
        padding: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background elements */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          right: "-10%",
          width: "600px",
          height: "600px",
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: "50%",
          filter: "blur(80px)",
          animation: "float 20s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-30%",
          left: "-10%",
          width: "500px",
          height: "500px",
          background: "rgba(255, 255, 255, 0.08)",
          borderRadius: "50%",
          filter: "blur(60px)",
          animation: "float 25s ease-in-out infinite reverse",
        }}
      />

      {/* Eventnova Background Text/Logo */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "clamp(120px, 25vw, 200px)",
          fontWeight: 900,
          color: "rgba(255, 255, 255, 0.03)",
          letterSpacing: "0.05em",
          whiteSpace: "nowrap",
          userSelect: "none",
          pointerEvents: "none",
          zIndex: 0,
          fontFamily: "system-ui, -apple-system, sans-serif",
          textTransform: "uppercase",
          animation: "textPulse 8s ease-in-out infinite",
        }}
      >
        EVENTNOVA
      </div>

      {/* Abstract geometric shapes */}
      <div
            style={{
          position: "absolute",
          top: "20%",
          right: "5%",
          width: "300px",
          height: "300px",
          border: "2px solid rgba(255, 255, 255, 0.05)",
          borderRadius: "50%",
          animation: "rotateSlow 30s linear infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "15%",
          right: "10%",
          width: "200px",
          height: "200px",
          border: "2px solid rgba(255, 255, 255, 0.04)",
          borderRadius: "20px",
          animation: "rotateSlowReverse 25s linear infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "30%",
          left: "8%",
          width: "150px",
          height: "150px",
          border: "2px solid rgba(255, 255, 255, 0.04)",
          borderRadius: "50%",
          animation: "rotateSlow 35s linear infinite",
        }}
      />

      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          paddingTop: "clamp(300px, 40vh, 420px)",
          paddingLeft: "16px",
          paddingRight: "16px",
          paddingBottom: "24px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Creative Hero Banner Section */}
        <div
          className="engagement-banner"
            style={{
            marginBottom: 24,
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 255, 0.95) 100%)",
            backdropFilter: "blur(20px)",
            borderRadius: "clamp(16px, 4vw, 24px)",
            padding: "clamp(20px, 4vw, 32px) clamp(16px, 3vw, 28px)",
            boxShadow:
              "0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.3)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative background elements */}
        <div
          style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "200px",
              height: "200px",
              background:
                "linear-gradient(135deg, rgba(108, 99, 255, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
              borderRadius: "50%",
              filter: "blur(40px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-30px",
              left: "-30px",
              width: "150px",
              height: "150px",
              background:
                "linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)",
              borderRadius: "50%",
              filter: "blur(30px)",
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            {/* Header Row */}
            <div
              className="engagement-banner-header"
              style={{
            display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 24,
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 24,
                    boxShadow: "0 8px 24px rgba(108, 99, 255, 0.4)",
                    position: "relative",
                  }}
                >
                  {userInitials}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background:
                        connectionStatus === "connected"
                          ? "#28a745"
                          : connectionStatus === "connecting"
                          ? "#FFD700"
                          : "#dc3545",
                      border: "3px solid #fff",
                      boxShadow:
                        connectionStatus === "connected"
                          ? "0 0 8px rgba(40, 167, 69, 0.6)"
                          : "none",
                      animation:
                        connectionStatus === "connected"
                          ? "pulse 2s ease-in-out infinite"
                          : "none",
                    }}
                  />
                </div>
                <div>
                  <h1
                    className="engagement-banner-title"
                    style={{
                      color: "#1a1a1a",
                      fontWeight: 800,
                      margin: 0,
                      fontSize: "clamp(20px, 4vw, 28px)",
                      letterSpacing: "-0.5px",
                      marginBottom: 4,
                    }}
                  >
                    EventNova Connect
                  </h1>
          </div>
              </div>
              <button
                onClick={() => navigate(-1)}
                style={{
                  background:
                    "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                  border: "none",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: "pointer",
                  padding: "12px 20px",
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 12px rgba(108, 99, 255, 0.3)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 16px rgba(108, 99, 255, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(108, 99, 255, 0.3)";
                }}
              >
                <FaArrowLeft /> Back
              </button>
            </div>

            {/* Stats/Info Cards Row */}
            <div
              className="engagement-banner-stats"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 12,
              }}
            >
              <div
                className="engagement-banner-stat"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(108, 99, 255, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                  borderRadius: "clamp(12px, 2vw, 16px)",
                  padding: "clamp(12px, 2vw, 16px)",
                  textAlign: "center",
                  border: "1px solid rgba(108, 99, 255, 0.2)",
                }}
              >
                <div
                  className="engagement-banner-stat-number"
                  style={{
                    fontSize: "clamp(18px, 3vw, 24px)",
                    fontWeight: 800,
                    color: "#6C63FF",
                    marginBottom: 4,
                  }}
                >
                  {chatMessages.length}
                </div>
                <div
                  className="engagement-banner-stat-label"
                  style={{
                    fontSize: "clamp(10px, 1.5vw, 12px)",
                    fontWeight: 600,
                    color: "#666",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Messages
                </div>
              </div>
              <div
                className="engagement-banner-stat"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                  borderRadius: "clamp(12px, 2vw, 16px)",
                  padding: "clamp(12px, 2vw, 16px)",
                  textAlign: "center",
                  border: "1px solid rgba(236, 72, 153, 0.2)",
                }}
              >
                <div
                  className="engagement-banner-stat-number"
                  style={{
                    fontSize: "clamp(18px, 3vw, 24px)",
                    fontWeight: 800,
                    color: "#EC4899",
                    marginBottom: 4,
                  }}
                >
                  {new Set(chatMessages.map((m) => m.senderId)).size}
                </div>
                <div
                  className="engagement-banner-stat-label"
                  style={{
                    fontSize: "clamp(10px, 1.5vw, 12px)",
                    fontWeight: 600,
                    color: "#666",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Participants
                </div>
              </div>
              <div
                className="engagement-banner-stat"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                  borderRadius: "clamp(12px, 2vw, 16px)",
                  padding: "clamp(12px, 2vw, 16px)",
                  textAlign: "center",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                }}
              >
                <div
                  className="engagement-banner-stat-number"
                  style={{
                    fontSize: "clamp(18px, 3vw, 24px)",
                    fontWeight: 800,
                    color: "#3B82F6",
                    marginBottom: 4,
                  }}
                >
                  {
                    chatMessages.filter(
                      (m) =>
                        m.messageType === "IMAGE" || m.messageType === "FILE" || m.messageType === "VOICE"
                    ).length
                  }
                </div>
                <div
                  className="engagement-banner-stat-label"
                  style={{
                    fontSize: "clamp(10px, 1.5vw, 12px)",
                    fontWeight: 600,
                    color: "#666",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Attachments
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Chat Card */}
        <div
          style={{
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            borderRadius: 24,
            boxShadow:
              "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)",
            padding: 0,
            minHeight: "calc(100vh - 200px)",
            maxHeight: "calc(100vh - 200px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Enhanced Chat Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "24px 20px",
              background:
                "linear-gradient(to bottom, #f8f9ff 0%, #ffffff 100%)",
              position: "relative",
            }}
          >
            {loadingMessages ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 24px rgba(108, 99, 255, 0.3)",
                  }}
                >
                  <FaSpinner
                    className="fa-spin"
                    style={{ fontSize: 24, color: "#fff" }}
                  />
                </div>
                <div
                  style={{ color: "#6C63FF", fontWeight: 600, fontSize: 16 }}
                >
                  Loading messages...
                </div>
              </div>
            ) : chatMessages.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 20,
                }}
              >
                <div
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 12px 40px rgba(108, 99, 255, 0.3)",
                    marginBottom: 8,
                  }}
                >
                  <FaCheckCircle style={{ fontSize: 48, color: "#fff" }} />
                </div>
                <div>
                  <h3
                    style={{
                      color: "#1a1a1a",
                      fontWeight: 800,
                      fontSize: 22,
                      marginBottom: 8,
                    }}
                  >
                    Start the Conversation!
                  </h3>
                  <p style={{ color: "#666", fontSize: 15, lineHeight: 1.6 }}>
                    No messages yet. Send a message to begin your engagement.
                  </p>
                </div>
              </div>
            ) : (
              sortedMessages.map((message, index) => {
                const isOwnMessage = message.senderId === userId;
                const senderInitials = (message.senderName ||
                  "U")[0].toUpperCase();
                return (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: isOwnMessage ? "flex-end" : "flex-start",
                      marginBottom: 20,
                      alignItems: "flex-end",
                      gap: 12,
                      animation: "messageSlideIn 0.15s ease-out",
                    }}
                  >
                    {!isOwnMessage && (
                    <div
                      style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: 800,
                          fontSize: 14,
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
                        }}
                      >
                        {senderInitials}
                      </div>
                    )}
                    <div
                      style={{
                        background: isOwnMessage
                          ? "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)"
                          : "#fff",
                        color: isOwnMessage ? "#fff" : "#1a1a1a",
                        borderRadius: isOwnMessage
                          ? "20px 20px 4px 20px"
                          : "20px 20px 20px 4px",
                        boxShadow: isOwnMessage
                          ? "0 4px 16px rgba(108, 99, 255, 0.3)"
                          : "0 2px 12px rgba(0, 0, 0, 0.08)",
                        padding: "14px 18px",
                        maxWidth: "65%",
                        minWidth: 100,
                        wordBreak: "break-word",
                        position: "relative",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isOwnMessage) {
                          e.currentTarget.style.boxShadow =
                            "0 4px 16px rgba(0, 0, 0, 0.12)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isOwnMessage) {
                          e.currentTarget.style.boxShadow =
                            "0 2px 12px rgba(0, 0, 0, 0.08)";
                        }
                      }}
                    >
                      {!isOwnMessage && (
                      <div
                        style={{
                          fontSize: 13,
                            color: "#8B5CF6",
                            marginBottom: 6,
                            fontWeight: 700,
                            letterSpacing: "0.3px",
                          }}
                        >
                          {message.senderName || "Unknown"}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 12,
                          color: isOwnMessage
                            ? "rgba(255, 255, 255, 0.85)"
                            : "#999",
                          marginBottom: 6,
                          fontWeight: 500,
                          fontStyle: "italic",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: isOwnMessage
                            ? "flex-end"
                            : "flex-start",
                        }}
                      >
                          {parseTimestamp(message.createdAt)}
                      </div>
                      {message.messageType === "TEXT" && (
                        <div
                          style={{
                            fontSize: 15,
                            lineHeight: 1.5,
                            color: isOwnMessage ? "#fff" : "#1a1a1a",
                          }}
                        >
                          {message.content}
                        </div>
                      )}
                      {message.messageType === "IMAGE" && (
                        <div style={{ marginTop: 6 }}>
                          <img
                            src={message.fileUrl || message.attachmentUrl}
                            alt="Attachment"
                            style={{
                              maxWidth: 220,
                              maxHeight: 180,
                              borderRadius: 8,
                              cursor: "pointer",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                            onClick={() =>
                              window.open(message.fileUrl || message.attachmentUrl, "_blank")
                            }
                          />
                          {message.content && (
                            <div style={{ marginTop: 4 }}>
                              {message.content}
                            </div>
                          )}
                        </div>
                      )}
                      {message.messageType === "FILE" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 6,
                          }}
                        >
                          <FaFile />
                          <a
                            href={message.fileUrl || message.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: isOwnMessage ? "#FFD700" : "#6C63FF",
                              fontWeight: 600,
                            }}
                          >
                            {message.content || "Attachment"}
                          </a>
                        </div>
                      )}
                      {message.messageType === "VOICE" && (
                        <VoiceMessagePlayer
                          audioUrl={message.fileUrl || message.attachmentUrl}
                          isOwnMessage={isOwnMessage}
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {otherUserTyping && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 12,
                  padding: "12px 18px",
                  background: "#fff",
                  borderRadius: 20,
                  maxWidth: "fit-content",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{ color: "#fff", fontSize: 12, fontWeight: 800 }}
                  >
                    V
                  </span>
                </div>
                <span style={{ color: "#666", fontWeight: 600, fontSize: 14 }}>
                  Vendor is typing
                </span>
                <div style={{ display: "flex", gap: 4, marginLeft: 4 }}>
                  <span
                    className="dot"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#8B5CF6",
                      animation: "typing 1.4s infinite",
                      opacity: 0,
                      animationDelay: "0s",
                    }}
                  />
                  <span
                    className="dot"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#8B5CF6",
                      animation: "typing 1.4s infinite",
                      opacity: 0,
                      animationDelay: "0.2s",
                    }}
                  />
                  <span
                    className="dot"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#8B5CF6",
                      animation: "typing 1.4s infinite",
                      opacity: 0,
                      animationDelay: "0.4s",
                    }}
                  />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {/* Enhanced Input Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              borderTop: "1px solid rgba(0, 0, 0, 0.06)",
              padding: "20px",
              background: "linear-gradient(to top, #ffffff 0%, #f8f9ff 100%)",
              borderBottomLeftRadius: 24,
              borderBottomRightRadius: 24,
              gap: 12,
            }}
          >
            <label
              htmlFor="fileInput"
              style={{
                cursor: uploadingFile || isRecording ? "not-allowed" : "pointer",
                padding: "10px",
                borderRadius: 12,
                background: uploadingFile || isRecording
                  ? "#f0f0f0"
                  : "linear-gradient(135deg, #f8f9ff 0%, #e0e7ff 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                if (!uploadingFile && !isRecording) {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #e0e7ff 0%, #d1d5f7 100%)";
                  e.currentTarget.style.transform = "scale(1.05)";
                }
              }}
              onMouseLeave={(e) => {
                if (!uploadingFile && !isRecording) {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #f8f9ff 0%, #e0e7ff 100%)";
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              <FaPaperclip
                color={uploadingFile || isRecording ? "#999" : "#6C63FF"}
                size={20}
              />
              <input
                id="fileInput"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileSelect}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                disabled={uploadingFile || isRecording}
              />
            </label>
            {!isRecording && !audioBlob && (
              <button
                onClick={startRecording}
                disabled={chatLoading || connectionStatus !== "connected" || isUploadingVoice}
                style={{
                  cursor: chatLoading || connectionStatus !== "connected" || isUploadingVoice
                    ? "not-allowed"
                    : "pointer",
                  padding: "10px",
                  borderRadius: 12,
                  background: chatLoading || connectionStatus !== "connected" || isUploadingVoice
                    ? "#f0f0f0"
                    : "linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!chatLoading && connectionStatus === "connected" && !isUploadingVoice) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(236, 72, 153, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!chatLoading && connectionStatus === "connected" && !isUploadingVoice) {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
              >
                <FaMicrophone
                  color={chatLoading || connectionStatus !== "connected" || isUploadingVoice ? "#999" : "#fff"}
                  size={20}
                />
              </button>
            )}
            {isRecording && (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  background: "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
                  borderRadius: 16,
                  border: "2px solid #F87171",
                }}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#EF4444",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                <span style={{ color: "#991B1B", fontWeight: 700, fontSize: 14 }}>
                  Recording... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")}
                </span>
                <button
                  onClick={stopRecording}
                  style={{
                    marginLeft: "auto",
                    padding: "6px 12px",
                    background: "#EF4444",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <FaStop size={12} />
                </button>
              </div>
            )}
            {audioBlob && !isRecording && (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  background: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)",
                  borderRadius: 16,
                  border: "2px solid #60A5FA",
                }}
              >
                <FaMicrophone color="#2563EB" size={18} />
                <span style={{ color: "#1E40AF", fontWeight: 600, fontSize: 14 }}>
                  Voice message ready ({Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")})
                </span>
                <audio src={audioUrl || undefined} controls style={{ display: "none" }} />
                <button
                  onClick={uploadAndSendVoiceMessage}
                  disabled={isUploadingVoice}
                  style={{
                    marginLeft: "auto",
                    padding: "6px 16px",
                    background: isUploadingVoice ? "#9CA3AF" : "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: isUploadingVoice ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {isUploadingVoice ? (
                    <>
                      <FaSpinner className="fa-spin" size={12} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane size={12} />
                      Send
                    </>
                  )}
                </button>
                <button
                  onClick={cancelRecording}
                  disabled={isUploadingVoice}
                  style={{
                    padding: "6px 10px",
                    background: "#F3F4F6",
                    color: "#6B7280",
                    border: "none",
                    borderRadius: 8,
                    cursor: isUploadingVoice ? "not-allowed" : "pointer",
                  }}
                >
                  <FaTimes size={12} />
                </button>
              </div>
            )}
            {!isRecording && !audioBlob && (
              <input
                type="text"
                value={chatInput}
                onChange={(e) => {
                  setChatInput(e.target.value);
                  if (!isTyping) {
                    setIsTyping(true);
                    sendTypingStatus(true);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") sendChatMessage();
                }}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  border: "2px solid #e0e7ff",
                  outline: "none",
                  fontSize: 15,
                  padding: "14px 18px",
                  borderRadius: 16,
                  background: "#fff",
                  color: "#1a1a1a",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#6C63FF";
                  e.currentTarget.style.boxShadow =
                    "0 0 0 4px rgba(108, 99, 255, 0.1)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#e0e7ff";
                  e.currentTarget.style.boxShadow = "none";
                  if (isTyping) {
                    setIsTyping(false);
                    sendTypingStatus(false);
                  }
                }}
                disabled={chatLoading || connectionStatus !== "connected"}
              />
            )}
            {!isRecording && !audioBlob && (
              <button
                onClick={selectedFile ? uploadAndSendFile : sendChatMessage}
                style={{
                  background:
                    chatInput.trim() || selectedFile
                      ? "linear-gradient(135deg, #6C63FF 0%, #8B5CF6 100%)"
                      : "linear-gradient(135deg, #e0e7ff 0%, #d1d5f7 100%)",
                  color: chatInput.trim() || selectedFile ? "#fff" : "#999",
                  border: "none",
                  borderRadius: 16,
                  padding: "14px 20px",
                  fontWeight: 700,
                  fontSize: 16,
                  cursor:
                    chatInput.trim() || selectedFile ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 56,
                  boxShadow:
                    chatInput.trim() || selectedFile
                      ? "0 4px 16px rgba(108, 99, 255, 0.4)"
                      : "none",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                }}
                disabled={
                  chatLoading ||
                  connectionStatus !== "connected" ||
                  (!chatInput.trim() && !selectedFile)
                }
                onMouseEnter={(e) => {
                  if (chatInput.trim() || selectedFile) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(108, 99, 255, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (chatInput.trim() || selectedFile) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 16px rgba(108, 99, 255, 0.4)";
                  }
                }}
              >
                {uploadingFile ? (
                  <FaSpinner className="fa-spin" />
                ) : (
                  <FaPaperPlane />
                )}
              </button>
            )}
          </div>
          {/* Selected file display */}
          {selectedFile && (
            <div
              style={{
                margin: 12,
                background: "#f8f8ff",
                borderRadius: 8,
                padding: 12,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <FaFile color="#6C63FF" />
              <span style={{ fontWeight: 600 }}>{selectedFile.name}</span>
              <span style={{ color: "#888", fontSize: 13 }}>
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </span>
              <button
                onClick={uploadAndSendFile}
                style={{
                  background: "#6C63FF",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "4px 10px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: uploadingFile ? "not-allowed" : "pointer",
                }}
                disabled={uploadingFile}
              >
                {uploadingFile ? <FaSpinner className="fa-spin" /> : "Send"}
              </button>
              <button
                onClick={() => setSelectedFile(null)}
                style={{
                  background: "#eee",
                  color: "#888",
                  border: "none",
                  borderRadius: 8,
                  padding: "4px 10px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: uploadingFile ? "not-allowed" : "pointer",
                }}
                disabled={uploadingFile}
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Enhanced Animations */}
      <style>{`
        /* Responsive styles */
        @media (max-width: 768px) {
          .engagement-banner {
            padding: 20px 16px !important;
          }
          .engagement-banner h1 {
            font-size: 22px !important;
          }
          .engagement-banner-stats {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
          }
          .engagement-banner-stat {
            padding: 12px 8px !important;
          }
          .engagement-banner-stat-number {
            font-size: 20px !important;
          }
          .engagement-banner-stat-label {
            font-size: 10px !important;
          }
        }
        @media (max-width: 480px) {
          .engagement-banner-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
          .engagement-banner-stats {
            grid-template-columns: 1fr !important;
          }
        }
        @keyframes typing {
          0%, 60%, 100% { 
            opacity: 0.3;
            transform: translateY(0);
          }
          30% { 
            opacity: 1;
            transform: translateY(-4px);
          }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(120deg); }
          66% { transform: translate(-20px, 20px) rotate(240deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }
        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes textPulse {
          0%, 100% {
            opacity: 0.03;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.06;
            transform: translate(-50%, -50%) scale(1.05);
          }
        }
        @keyframes rotateSlow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes rotateSlowReverse {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(-360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default EngagementChatPage;
